import { useState } from 'react';
import type { Device, Extension, ProvisioningPreview } from '@arcnet/contracts';
import { useMutation } from '@tanstack/react-query';
import {
  useDevices, useOpenProvisioning, useSaveDevice, useDeleteDevice,
} from '../../lib/api/hooks-extension.js';
import { usePreviewConfig, useProvisioningRoles, useProvisioningUrl } from '../../lib/api/hooks-provisioning.js';
import { DeviceEditor } from './DeviceEditor.js';
import { HotDeskLogin } from './HotDeskLogin.js';
import { VENDOR_LABEL, Window, Preview } from './ProvisioningBits.js';
import { Badge, ErrorBanner, StatusBadge } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Setting up the phones for ONE seat.
 *
 * The account-wide Provisioning page is the right screen for "which handsets
 * have never fetched a configuration"; it is the wrong one for the job actually
 * in front of somebody, which is a named person, a phone on their desk and a
 * mobile in their hand. That job needs four things together -- the MAC, the
 * make, what the phone will be served, and the app's QR code -- and they were
 * spread across three screens, two of which are in the other console.
 *
 * Handsets are set up here and nowhere else since the account-wide Handsets list
 * went (owner, 15 September 2026): the form, silencing, the shared desk and
 * removal. The window and preview are the same components the Provisioning page
 * uses, and the QR is the same component as before.
 *
 * One card for the desk phones, each handset a row in it, with a line of text
 * rather than a large empty state when there are none: the section is one of
 * three cards in Phone setup, and an empty state the height of a page pushed
 * the app's card off the screen (15 September 2026).
 */
export function ExtensionProvisioningTab({ tenantId, ext }: {
  tenantId: string; ext: Extension;
}) {
  const can = useSession((s) => s.can);
  const devices = useDevices(tenantId);
  const open = useOpenProvisioning(tenantId);
  const save = useSaveDevice(tenantId);
  const del = useDeleteDevice(tenantId);
  /**
   * Shared desk, per handset: whether anybody may log in at it with *31. A
   * property of the phone; turning it off ends any login on it (the API does
   * that), so nobody is left speaking as someone who cannot log out.
   */
  const desk = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api.patch(`/v1/tenants/${tenantId}/hot-desk/devices/${id}`, { hot_desk_enabled: on }),
    onSuccess: () => void devices.refetch(),
  });
  const preview = usePreviewConfig(tenantId);
  const roles = useProvisioningRoles();
  const step = useStepUp();
  const [editing, setEditing] = useState<Device | 'new' | null>(null);
  const [shown, setShown] = useState<{ deviceId: string; data: ProvisioningPreview } | null>(null);

  const mayWrite = can('device:write');
  const mayOpen = can('device:provision');
  const mayReveal = can('extension:credentials:reveal');
  /** One address for every phone: the provisioning name (API provisioning urls.ts). */
  const genericUrl = useProvisioningUrl();

  /**
   * This seat's handsets, found through the line keys rather than a field on the
   * device: a phone can carry several extensions, and an extension can appear on
   * several phones. "Whose phone is this" has no single answer, and the line key
   * is the only place the real one is recorded.
   */
  const mine = (devices.data?.data ?? [])
    .filter((d) => d.status !== 'retired' && d.lines.some((l) => l.extension_id === ext.id));

  const load = async (deviceId: string, reveal: boolean) => {
    await step.run(async () => {
      const data = await preview.mutateAsync({ deviceId, reveal });
      setShown({ deviceId, data });
      return data;
    });
  };

  return (
    <>
      {/* The handset form is a card of its own (DeviceEditor), so it opens above
          the list rather than inside it. */}
      {editing && (
        <DeviceEditor
          tenantId={tenantId}
          existing={editing === 'new' ? null : editing}
          seedExtensionId={ext.id}
          onDone={() => setEditing(null)}
        />
      )}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">Desk phones</h2>
          {mayWrite && !editing && (
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={12} /> Add phone
            </button>
          )}
        </div>
        <div className="arc-card-body">
          {devices.error && <ErrorBanner error={devices.error} />}
          <StepUpPrompt step={step} what="Showing a configuration with its SIP password" />

          {/* Said where somebody adding a desk phone is looking: the address a
              redirect service or DHCP option 66 needs is the same for every phone. */}
          <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            <span className="arc-muted" style={{ fontSize: 12.5 }}>Provisioning URL</span>
            <code className="arc-mono" style={{ fontSize: 12, userSelect: 'all' }}>{genericUrl}</code>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => void navigator.clipboard?.writeText(genericUrl)}>
              Copy
            </button>
            <span className="arc-subtle" style={{ fontSize: 11.5 }}>
              for any phone: Yealink RPS, Fanvil FDPS or DHCP option 66
            </span>
          </div>

          {devices.isLoading && <div className="arc-skeleton" style={{ height: 40 }} />}

          {mine.map((d) => {
            const line = d.lines.find((l) => l.extension_id === ext.id);
            return (
              <div key={d.id} className="arc-card-row">
                <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start' }}>
                  <div className="arc-stack" style={{ gap: 2 }}>
                    <span style={{ fontWeight: 550 }}>
                      {d.label ?? VENDOR_LABEL[d.vendor] ?? 'Handset'}
                    </span>
                    <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                      {VENDOR_LABEL[d.vendor] ?? d.vendor}{d.model ? ` ${d.model}` : ''}
                      {d.template
                        ? ` \u00b7 ${d.template.name}`
                        : d.provisioning_role && d.provisioning_role !== 'back_office'
                          ? ` \u00b7 ${roles.data?.data.find((r) => r.role === d.provisioning_role)?.name ?? d.provisioning_role}`
                          : ''}
                      {/* Which key on the phone this seat sits on. A handset with
                          four lines is common and the seat is rarely the first. */}
                      {line ? ` \u00b7 line ${line.line_number}${d.lines.length > 1 ? ` of ${d.lines.length}` : ''}` : ''}
                    </span>
                  </div>
                  <div className="arc-spacer" />
                  {/* Silenced is not offline: it stays registered and calls out, and
                      is left out of the extension's ring. */}
                  {d.ring_enabled === false && (
                    <span className="arc-subtle" style={{ fontSize: 11.5 }}>does not ring</span>
                  )}
                  {/* The phone named another model when it last fetched: its template may not fit it. */}
                  {d.reported_model && d.model && d.reported_model.toUpperCase() !== d.model.toUpperCase() && (
                    <Badge tone="warn">phone reports {d.reported_model}</Badge>
                  )}
                  <StatusBadge status={d.status} />
                </div>

                <dl className="arc-kv" style={{ marginTop: 10 }}>
                  <dt>MAC address</dt>
                  <dd className="arc-mono">
                    {d.mac_address ?? <span className="arc-subtle">{'not set \u2014 this phone cannot provision'}</span>}
                  </dd>
                  <dt>Template</dt>
                  <dd>
                    {d.template
                      ? <>
                          {d.template.name} · v{d.template.version}
                          {d.template_version_served !== null && d.template_version_served !== undefined
                            && d.template_version_served < d.template.version && (
                            <span className="arc-subtle">{` \u00b7 v${d.template_version_served} on the phone until it fetches`}</span>
                          )}
                        </>
                      : <span className="arc-subtle">none — laid out by its role</span>}
                  </dd>
                  <dt>Last fetched</dt>
                  <dd>{d.provisioned_at ? relativeTime(d.provisioned_at) : <span className="arc-subtle">never</span>}</dd>
                  <dt>May fetch now</dt>
                  <dd><Window device={d} /></dd>
                  <dt>Shared desk</dt>
                  <dd>
                    <Switch id={`desk-${d.id}`} checked={d.hot_desk_enabled === true}
                      disabled={!mayWrite || desk.isPending}
                      label={`${d.label ?? d.mac_address ?? 'Handset'} accepts hot desk logins`}
                      onChange={(on) => {
                        if (on || confirm(`Stop ${d.label ?? d.mac_address ?? 'this handset'} being a shared desk? Anyone logged in at it will be logged out.`)) {
                          desk.mutate({ id: d.id, on });
                        }
                      }} />
                  </dd>
                </dl>

                <div className="arc-row arc-row-actions" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                  {mayWrite && (
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                      onClick={() => setEditing(d)}>
                      Edit phone
                    </button>
                  )}
                  {mayWrite && (
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                      disabled={save.isPending}
                      title={d.ring_enabled === false
                        ? 'Include this handset in the extension ring again'
                        : 'Stop this handset ringing, without disconnecting it'}
                      onClick={() => save.mutate({ id: d.id, ring_enabled: d.ring_enabled === false })}>
                      {d.ring_enabled === false ? 'Ring again' : 'Silence'}
                    </button>
                  )}
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                    disabled={preview.isPending}
                    onClick={() => {
                      if (shown?.deviceId === d.id) setShown(null);
                      else void load(d.id, false);
                    }}>
                    {shown?.deviceId === d.id ? 'Hide configuration' : 'Show configuration'}
                  </button>
                  {mayOpen && (
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                      disabled={open.isPending}
                      title="Let this handset fetch its configuration again, for 30 minutes"
                      onClick={() => {
                        if (confirm(`Allow ${d.label ?? d.mac_address ?? 'this handset'} to provision for the next 30 minutes? The URL does not change.`)) {
                          open.mutate(d.id);
                        }
                      }}>
                      Allow provisioning
                    </button>
                  )}
                  {mayWrite && (
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                      disabled={del.isPending}
                      onClick={() => {
                        const others = d.lines.length - 1;
                        if (confirm(`Remove ${d.label ?? d.mac_address ?? 'this handset'}? The phone stops receiving its configuration${others > 0 ? `, and its other ${others === 1 ? 'line goes' : `${others} lines go`} with it` : ''}.`)) {
                          del.mutate(d.id);
                        }
                      }}>
                      Remove phone
                    </button>
                  )}
                </div>

                {shown?.deviceId === d.id && (
                  <div style={{
                    marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--arc-border)',
                  }}>
                    <Preview data={shown.data} canReveal={mayReveal}
                      onReveal={() => void load(d.id, true)} />
                  </div>
                )}
              </div>
            );
          })}

          {!devices.isLoading && mine.length === 0 && (
            <p className="arc-muted" style={{ margin: 0, fontSize: 12.5 }}>
              No desk phone on {ext.number} yet. Add one by its MAC address and it fetches its own configuration.
            </p>
          )}
        </div>
      </div>

      {/* What this person types at a shared desk: their extension and a PIN. */}
      <HotDeskLogin tenantId={tenantId} ext={ext} mayWrite={mayWrite} />
    </>
  );
}
